import React, { useRef, useEffect, useCallback } from 'react';

export default function SpotlightEffect({
  children,
  color = 'rgba(238, 58, 71, 0.08)',
  radius = 300,
  lerp = 0.15,
  className = '',
}) {
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const activeRef = useRef(false);

  const animate = useCallback(() => {
    const dx = mouseRef.current.x - currentRef.current.x;
    const dy = mouseRef.current.y - currentRef.current.y;
    currentRef.current.x += dx * lerp;
    currentRef.current.y += dy * lerp;

    if (overlayRef.current) {
      overlayRef.current.style.setProperty('--spot-x', `${currentRef.current.x}px`);
      overlayRef.current.style.setProperty('--spot-y', `${currentRef.current.y}px`);
    }

    rafRef.current = requestAnimationFrame(animate);
  }, [lerp]);

  useEffect(() => {
    const hasPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!hasPointer || prefersReduced) return;

    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      if (!activeRef.current) {
        activeRef.current = true;
        if (overlayRef.current) overlayRef.current.style.opacity = '1';
      }
    };

    const handleMouseLeave = () => {
      activeRef.current = false;
      if (overlayRef.current) overlayRef.current.style.opacity = '0';
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    background: `radial-gradient(${radius}px circle at var(--spot-x, 50%) var(--spot-y, 50%), ${color}, transparent)`,
    zIndex: 1,
  };

  return (
    <div ref={containerRef} className={className} style={{ position: 'relative' }}>
      <div ref={overlayRef} style={overlayStyle} />
      {children}
    </div>
  );
}
